import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useT, DISCLOSURE_VERSION } from '../lib/i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCTS = [
  { slug: 'auto', emoji: '🚗', labelKey: 'product_auto', featured: true },
  { slug: 'home', emoji: '🏡', labelKey: 'product_home', featured: true },
  { slug: 'bundle', emoji: '🚗🏡', labelKey: 'product_bundle', featured: true },
  { slug: 'renters', emoji: '🏘️', labelKey: 'product_renters', featured: false },
  { slug: 'business', emoji: '🏢', labelKey: 'product_business', featured: false },
  { slug: 'life', emoji: '❤️', labelKey: 'product_life', featured: false, subtle: true },
];

// Map campaign product slug to backend coverage_type (which only knows the 8 core slugs)
const PRODUCT_TO_COVERAGE = {
  auto: 'auto',
  home: 'home',
  bundle: 'auto', // bundle logged as auto; "bundle_interest" captured in notes
  renters: 'renters',
  business: 'business',
  life: 'life',
};

const BEST_TIMES = ['Morning (8am – 12pm)', 'Afternoon (12pm – 5pm)', 'Evening (5pm – 8pm)', 'Anytime'];
const BEST_TIMES_ES = ['Mañana (8am – 12pm)', 'Tarde (12pm – 5pm)', 'Noche (5pm – 8pm)', 'Cualquier hora'];

const PHONE_RE = /^[+()\-\s\d]{7,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_RE = /^\d{5}$/;

const CampaignLanding = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [lang, t, setLang] = useT(params.get('lang'));

  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    zip_code: '',
    product: '',
    current_carrier: '',
    policy_expiration_date: '',
    preferred_contact_method: 'phone',
    best_time_to_contact: '',
    notes: '',
    // honeypot — must stay empty
    website_url: '',
  });
  const [consent, setConsent] = useState({
    contact: false,
    marketing_email: false,
    marketing_sms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [fieldErrs, setFieldErrs] = useState({});

  // Capture UTM once
  const utm = useMemo(() => ({
    source: params.get('utm_source') || '',
    medium: params.get('utm_medium') || '',
    campaign: params.get('utm_campaign') || '',
    content: params.get('utm_content') || '',
    term: params.get('utm_term') || '',
  }), [params]);

  const update = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setFieldErrs((e) => ({ ...e, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!data.first_name.trim()) e.first_name = t('err_required');
    if (!data.last_name.trim()) e.last_name = t('err_required');
    if (!data.phone.trim()) e.phone = t('err_required');
    else if (!PHONE_RE.test(data.phone)) e.phone = t('err_phone_invalid');
    if (!data.email.trim()) e.email = t('err_required');
    else if (!EMAIL_RE.test(data.email)) e.email = t('err_email_invalid');
    if (!data.zip_code.trim()) e.zip_code = t('err_required');
    else if (!ZIP_RE.test(data.zip_code.trim())) e.zip_code = t('err_zip_invalid');
    if (!data.product) e.product = t('err_required');
    setFieldErrs(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError('');
    const fieldsOk = validate();
    if (!consent.contact) {
      setError(t('err_consent_required'));
      return;
    }
    if (!fieldsOk) return;
    setSubmitting(true);
    try {
      const coverage_type = PRODUCT_TO_COVERAGE[data.product] || 'auto';
      const payload = {
        coverage_type,
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        phone: data.phone.trim(),
        email: data.email.trim(),
        zip_code: data.zip_code.trim(),
        best_time_to_contact: data.best_time_to_contact,
        notes: [
          data.product === 'bundle' ? '[Bundle: Home + Auto]' : '',
          data.notes.trim(),
        ].filter(Boolean).join(' — '),
        consent: true, // legacy single-boolean kept true for backend contract compatibility
        source: 'Website',

        // Phase 1 additions:
        language: lang,
        request_type: 'quote',
        campaign_product: data.product,
        current_carrier: data.current_carrier.trim(),
        policy_expiration_date: data.policy_expiration_date,
        preferred_contact_method: data.preferred_contact_method,
        consent_record: {
          contact: consent.contact,
          marketing_email: consent.marketing_email,
          marketing_sms: consent.marketing_sms,
          disclosure_version: DISCLOSURE_VERSION,
          language: lang,
          submitted_at: new Date().toISOString(),
        },
        utm,
        // Honeypot — if a bot filled it the backend will reject silently
        website_url: data.website_url,
      };
      const res = await axios.post(`${API}/leads`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });
      if (!res?.data?.ok) throw new Error('Submission did not confirm success.');
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Campaign lead submission failed:', err);
      if (err?.response?.status === 429) setError(t('err_rate_limit'));
      else setError(t('err_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  // Keep page lang in sync with URL param if the user changes it
  useEffect(() => {
    const p = params.get('lang');
    if (p && p !== lang) setLang(p);
  }, [params]);

  // ===== Confirmation =====
  if (result) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-3xl" data-testid="campaign-confirmation">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#01325B' }} data-testid="confirmation-title">
            {t('thank_title')}
          </h1>
          <p className="text-gray-700 mb-8 text-lg">{t('thank_body')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/quote/callback?lang=${lang}`} data-testid="confirmation-callback" className="px-6 py-3 rounded-lg font-semibold text-white" style={{ background: '#E07A2C' }}>
              {t('thank_callback_cta')}
            </Link>
            <Link to="/protection-center" data-testid="confirmation-back" className="px-6 py-3 rounded-lg font-semibold border-2 inline-flex items-center justify-center" style={{ borderColor: '#01325B', color: '#01325B' }}>
              {t('thank_back')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const timeOptions = lang === 'es' ? BEST_TIMES_ES : BEST_TIMES;

  return (
    <div data-testid="campaign-landing-page">
      {/* Language toggle bar */}
      <div className="bg-[#01325B] text-white">
        <div className="container mx-auto px-4 py-2 flex items-center justify-end gap-3 text-sm">
          <button
            type="button"
            onClick={() => setLang('en')}
            data-testid="lang-toggle-en"
            className={`px-3 py-1 rounded ${lang === 'en' ? 'bg-[#E07A2C] font-semibold' : 'hover:underline'}`}
            aria-pressed={lang === 'en'}
          >
            {t('lang_english')}
          </button>
          <span aria-hidden="true" className="opacity-60">|</span>
          <button
            type="button"
            onClick={() => setLang('es')}
            data-testid="lang-toggle-es"
            className={`px-3 py-1 rounded ${lang === 'es' ? 'bg-[#E07A2C] font-semibold' : 'hover:underline'}`}
            aria-pressed={lang === 'es'}
          >
            {t('lang_spanish')}
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#01325B 0%, #02466e 50%, #E07A2C 160%)' }}>
        <div className="container mx-auto px-4 py-16 md:py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-block text-xs tracking-[0.25em] uppercase opacity-80 mb-3">
              {t('hero_eyebrow')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5" data-testid="hero-headline">
              {t('hero_headline')}
            </h1>
            <p className="text-lg md:text-xl opacity-90 mb-7 leading-relaxed">{t('hero_sub')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#form"
                data-testid="hero-cta-form"
                className="inline-flex items-center justify-center px-7 py-4 rounded-lg font-semibold text-base shadow-lg transition-transform hover:scale-[1.02]"
                style={{ background: '#E07A2C', color: 'white' }}
              >
                {t('hero_cta_scroll')}
              </a>
              <Link
                to={`/quote/callback?lang=${lang}`}
                data-testid="hero-cta-callback"
                className="inline-flex items-center justify-center px-7 py-4 rounded-lg font-semibold text-base border-2 border-white/70 hover:bg-white hover:text-[#01325B] transition-colors"
              >
                {t('hero_cta_callback')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: '#01325B' }}>{t('how_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-[#f6f5f1] rounded-xl p-6 border border-gray-200">
                <div className="w-10 h-10 rounded-full text-white font-bold flex items-center justify-center mb-3" style={{ background: '#E07A2C' }}>{n}</div>
                <div className="font-bold text-lg mb-1" style={{ color: '#01325B' }}>{t(`how_${n}_title`)}</div>
                <div className="text-sm text-gray-700">{t(`how_${n}_desc`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="form" className="py-14 bg-[#f6f5f1] scroll-mt-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#01325B' }}>{t('form_title')}</h2>
            <p className="text-gray-600">{t('form_sub')}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-5" data-testid="campaign-form" noValidate>
            {/* Honeypot — visually hidden, bots fill it */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={data.website_url}
                  onChange={(e) => update('website_url', e.target.value)}
                  data-testid="honeypot-field"
                />
              </label>
            </div>

            {/* Products */}
            <div>
              <label className="block text-sm font-semibold mb-3" style={{ color: '#01325B' }}>{t('f_product')} *</label>
              <p className="text-xs text-gray-500 mb-3">{t('products_hint')}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {PRODUCTS.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => update('product', p.slug)}
                    data-testid={`product-${p.slug}`}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                      data.product === p.slug
                        ? 'border-[#E07A2C] bg-orange-50'
                        : p.featured
                        ? 'border-[#01325B]/40 bg-white hover:border-[#E07A2C]'
                        : 'border-gray-200 bg-white hover:border-[#E07A2C]'
                    } ${p.subtle ? 'opacity-95' : ''}`}
                    aria-pressed={data.product === p.slug}
                  >
                    {p.featured && (
                      <span className="absolute top-1 right-2 text-[10px] font-bold text-[#E07A2C] uppercase tracking-wide">
                        {t('product_featured_badge')}
                      </span>
                    )}
                    <div className="text-2xl mb-1">{p.emoji}</div>
                    <div className={`font-semibold text-sm ${p.subtle ? 'text-gray-700' : 'text-[#01325B]'}`}>{t(p.labelKey)}</div>
                  </button>
                ))}
              </div>
              {fieldErrs.product && <p className="text-sm text-red-600 mt-2" data-testid="err-product">{fieldErrs.product}</p>}
            </div>

            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('f_first_name') + ' *'} err={fieldErrs.first_name} testid="field-first-name">
                <input className="input" value={data.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="input-first-name" />
              </Field>
              <Field label={t('f_last_name') + ' *'} err={fieldErrs.last_name} testid="field-last-name">
                <input className="input" value={data.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="input-last-name" />
              </Field>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('f_phone') + ' *'} err={fieldErrs.phone} testid="field-phone">
                <input type="tel" className="input" value={data.phone} onChange={(e) => update('phone', e.target.value)} data-testid="input-phone" />
              </Field>
              <Field label={t('f_email') + ' *'} err={fieldErrs.email} testid="field-email">
                <input type="email" className="input" value={data.email} onChange={(e) => update('email', e.target.value)} data-testid="input-email" />
              </Field>
            </div>

            {/* ZIP */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('f_zip') + ' *'} err={fieldErrs.zip_code} testid="field-zip">
                <input inputMode="numeric" maxLength={5} className="input" value={data.zip_code} onChange={(e) => update('zip_code', e.target.value.replace(/[^0-9]/g, ''))} data-testid="input-zip" />
              </Field>
              <Field label={t('f_preferred_contact')} testid="field-contact-method">
                <select className="input" value={data.preferred_contact_method} onChange={(e) => update('preferred_contact_method', e.target.value)} data-testid="input-contact-method">
                  <option value="phone">{t('f_contact_phone')}</option>
                  <option value="text">{t('f_contact_text')}</option>
                  <option value="email">{t('f_contact_email')}</option>
                </select>
              </Field>
            </div>

            {/* Optional */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('f_current_carrier')} testid="field-carrier">
                <input className="input" value={data.current_carrier} onChange={(e) => update('current_carrier', e.target.value)} data-testid="input-carrier" />
              </Field>
              <Field label={t('f_policy_expiration')} testid="field-expiration">
                <input type="date" className="input" value={data.policy_expiration_date} onChange={(e) => update('policy_expiration_date', e.target.value)} data-testid="input-expiration" />
              </Field>
            </div>

            <Field label={t('f_best_time')} testid="field-best-time">
              <select className="input" value={data.best_time_to_contact} onChange={(e) => update('best_time_to_contact', e.target.value)} data-testid="input-best-time">
                <option value="">—</option>
                {timeOptions.map((v, i) => (<option key={v} value={BEST_TIMES[i]}>{v}</option>))}
              </select>
            </Field>

            <Field label={t('f_notes')} testid="field-notes">
              <textarea rows={3} className="input" value={data.notes} onChange={(e) => update('notes', e.target.value)} data-testid="input-notes" />
            </Field>

            {/* Consent */}
            <div className="bg-[#f6f5f1] rounded-xl p-4 border border-gray-200">
              <div className="text-sm font-semibold mb-3" style={{ color: '#01325B' }}>{t('consent_title')}</div>

              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={consent.contact}
                  onChange={(e) => { setConsent((c) => ({ ...c, contact: e.target.checked })); if (e.target.checked) setError(''); }}
                  data-testid="consent-contact"
                  required
                />
                <span className="text-sm text-gray-800">
                  {t('consent_required_label')}
                  <span className="block text-xs text-gray-500 italic">{t('consent_required_help')}</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={consent.marketing_email}
                  onChange={(e) => setConsent((c) => ({ ...c, marketing_email: e.target.checked }))}
                  data-testid="consent-marketing-email"
                />
                <span className="text-sm text-gray-800">
                  {t('consent_marketing_email_label')}
                  <span className="block text-xs text-gray-500 italic">{t('consent_optional_help')}</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={consent.marketing_sms}
                  onChange={(e) => setConsent((c) => ({ ...c, marketing_sms: e.target.checked }))}
                  data-testid="consent-marketing-sms"
                />
                <span className="text-sm text-gray-800">
                  {t('consent_marketing_sms_label')}
                  <span className="block text-xs text-gray-500 italic">{t('consent_optional_help')}</span>
                </span>
              </label>

              <p className="text-xs text-gray-500 mt-3">
                <Link to="/privacy" className="underline hover:text-[#E07A2C]">{t('privacy_link')}</Link>
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="submit-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-btn"
              className="w-full px-6 py-4 rounded-lg font-semibold text-white text-base disabled:opacity-60"
              style={{ background: '#E07A2C' }}
            >
              {submitting ? t('f_submitting') : t('f_submit')}
            </button>

            <p className="text-xs text-gray-500 italic text-center">
              {t('disclaimer_savings')}
            </p>
          </form>

          {/* Life mention — subtle */}
          <p className="text-center text-sm text-gray-600 italic mt-8" data-testid="life-mention">
            {t('life_mention')}
          </p>
        </div>
      </section>

      {/* Agency contact (bottom) */}
      <section className="py-10" style={{ background: '#01325B' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <div className="text-xl font-bold mb-1">Here Comes Protection</div>
          <div className="italic opacity-90 mb-3 text-sm">{t('slogan')}</div>
          <div className="text-sm">
            <span className="opacity-80">{t('contact_email')}: </span>
            <a href="mailto:Herecomesprotection@gmail.com" className="underline font-semibold">Herecomesprotection@gmail.com</a>
          </div>
          <div className="text-sm">
            <span className="opacity-80">{t('contact_website')}: </span>
            <a href="https://herecomesprotection.com" className="underline font-semibold">herecomesprotection.com</a>
          </div>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 0.65rem 0.85rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 1rem;
          background: white;
          color: #111;
        }
        .input:focus {
          outline: 2px solid #E07A2C;
          outline-offset: 1px;
          border-color: #E07A2C;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, err, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{label}</label>
    {children}
    {err && <p className="text-sm text-red-600 mt-1" data-testid={`${testid}-err`}>{err}</p>}
  </div>
);

export default CampaignLanding;
